import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { User } from 'lucide-react';
import { doctors } from '@/data/doctors';
import { useNavigate } from 'react-router-dom';

interface TeamListProps {
  onSelectDoctor: (doctorId: string) => void;
}

// Define department types
type Department = {
  id: string;
  nameRu: string;
  nameKz: string;
  order: number;
}

const TeamList: React.FC<TeamListProps> = ({ onSelectDoctor }) => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  // Define departments - only medical and other staff, remove management
  const departments: Department[] = [
    { id: 'sportmed', nameRu: 'Отделение спортивной медицины и медико-биологического обеспечения', nameKz: 'Спорт медицинасы және медико-биологиялық қамтамасыз ету бөлімі', order: 1 },
    { id: 'specialists', nameRu: 'Отделение специалистов по профилю', nameKz: 'Мамандандырылған дәрігерлер бөлімі', order: 2 },
    { id: 'diagnostics', nameRu: 'Отделение функциональной диагностики', nameKz: 'Функционалды диагностика бөлімі', order: 3 },
    { id: 'physiotherapy', nameRu: 'Отделение физиотерапии, лечебной физкультуры и массажа', nameKz: 'Физиотерапия, емдік дене шынықтыру және массаж бөлімі', order: 4 },
    { id: 'nursing', nameRu: 'Средний медперсонал', nameKz: 'Орта медицина қызметкерлері', order: 5 },
    { id: 'rehabilitation', nameRu: 'Отделение физиотерапии, лечебной физкультуры и массажа', nameKz: 'Физиотерапия, емдік дене шынықтыру және массаж бөлімі', order: 6 },
    { id: 'other', nameRu: 'Прочие специалисты', nameKz: 'Басқа мамандар', order: 7 },
  ];

  // Group doctors by department
  const doctorsByDepartment: { [key: string]: any[] } = {};
  
  // Initialize empty arrays for each department
  departments.forEach(dept => {
    doctorsByDepartment[dept.id] = [];
  });

  // Define staff with proper hierarchy - only non-management staff
  const staff = [
    // Отделение спортивной медицины и медико-биологического отделения
    { id: "tynyshtykova", name: { ru: "Тыныштықова Ляззат Елубаевна", kz: "Тыныштықова Ляззат Елубаевна" }, position: { ru: "Медицинская сестра", kz: "Мейірбике" }, department: "sportmed", order: 6 },
    { id: "anes", name: { ru: "Анес Назерке Мендуллаевна", kz: "Анес Назерке Мендуллаевна" }, position: { ru: "Медицинская сестра / Сестринское дело", kz: "Медбике / Сестрин ісі" }, department: "sportmed", order: 7 },
    { id: "kolkhozov", name: { ru: "Колхозов Айдар Оразбекович", kz: "Колхозов Айдар Оразбекович" }, position: { ru: "Фельдшер выездной бригады скорой медицинской помощи", kz: "Жедел жәрдем бригадасының фельдшері" }, department: "sportmed", order: 8 },
    { id: "boltaev", name: { ru: "Болтаев Олжас Талгатович", kz: "Болтаев Олжас Талгатович" }, position: { ru: "Врач спортивной медицины", kz: "Спорт медицинасы дәрігері" }, department: "sportmed", order: 2 },
    { id: "nugmetollanova", name: { ru: "Нығметолланова Сәния Миржанқызы", kz: "Нығметолланова Сәния Миржанқызы" }, position: { ru: "Врач-эндокринолог", kz: "Эндокринолог дәрігері" }, department: "sportmed", order: 4 },
    { id: "kozhakhmetova", name: { ru: "Кожахметова Жанна Ербулатовна", kz: "Кожахметова Жанна Ербулатовна" }, position: { ru: "Спортивный психолог", kz: "Спорт психологы" }, department: "sportmed", order: 5 },
    { id: "erbolatova", name: { ru: "Ерболатова Гульжаухар Мирамбековна", kz: "Ерболатова Гульжаухар Мирамбековна" }, position: { ru: "Спортивный врач", kz: "Спорт дәрігері" }, department: "sportmed", order: 9 },
    { id: "abaeva", name: { ru: "Абаева Ажар Поштайқызы", kz: "Абаева Ажар Поштайқызы" }, position: { ru: "Врач спортивной медицины (резидентура)", kz: "Спорт медицинасы дәрігері (резидентура)" }, department: "sportmed", order: 10 },
    { id: "zhanuzakova", name: { ru: "Жанузакова Амангуль Серикбаевна", kz: "Жанұзақова Амангүл Серікбайқызы" }, position: { ru: "Врач-эпидемиолог", kz: "Эпидемиолог дәрігер" }, department: "sportmed", order: 11 },

    // Мамандандырылған дәрігерлер бөлімі
    { id: "akhmetova", name: { ru: "Ахметова Гульнара Умурзаковна", kz: "Ахметова Гүлнара Умурзақызы" }, position: { ru: "Врач-оториноларинголог высшей категории", kz: "Жоғары санатты оториноларинголог дәрігері" }, department: "specialists", order: 1 },
    { id: "toygulova", name: { ru: "Тойгулова Динара Баймухановна", kz: "Тойгулова Динара Баймухановна" }, position: { ru: "Стоматолог", kz: "Стоматолог" }, department: "specialists", order: 2 },
    { id: "makenqyzy", name: { ru: "Макенқызы Ақерке", kz: "Макенқызы Ақерке" }, position: { ru: "Офтальмолог", kz: "Офтальмолог" }, department: "specialists", order: 3 },
    { id: "utesheva", name: { ru: "Утешева Сауле Шакеновна", kz: "Утешева Сауле Шакеновна" }, position: { ru: "Гинеколог", kz: "Гинеколог" }, department: "specialists", order: 4 },
    { id: "nartbaev", name: { ru: "Нартбаев Есмұрат Кажымуханович", kz: "Нартбаев Есмұрат Кажымуханович" }, position: { ru: "Хирург – травматолог", kz: "Хирург – травматолог" }, department: "specialists", order: 5 },
    { id: "zhaparova", name: { ru: "Жапарова Айжан Мухтаровна", kz: "Жапарова Айжан Мұхтарқызы" }, position: { ru: "Врач-кардиолог", kz: "Кардиолог дәрігері" }, department: "specialists", order: 6 },
    { id: "gulzhaina", name: { ru: "Гульжайна Санкей", kz: "Гүлжайна Санкей" }, position: { ru: "Врач-невропатолог", kz: "Невропатолог дәрігер" }, department: "specialists", order: 7 },
    { id: "nurzhauova", name: { ru: "Нуржауова Нурлыкыз Кадирбеккызы", kz: "Нұржауова Нұрлыкыз Қадірбекқызы" }, position: { ru: "Врач функциональной диагностики", kz: "Функционалдық диагностика дәрігері" }, department: "diagnostics", order: 8 },
    { id: "sovetov", name: { ru: "Советов Мерхат Мұратұлы", kz: "Советов Мерхат Мұратұлы" }, position: { ru: "Врач-реабилитолог отделения физиотерапии, лечебной физкультуры и массажа", kz: "Физиотерапия, емдік дене шынықтыру және массаж бөлімінің дәрігер-реабилитологы" }, department: "physiotherapy", order: 1 },
    { id: "saitgaraev", name: { ru: "Сайтгараев Мерден Серикович", kz: "Сайтгараев Мерден Серікұлы" }, position: { ru: "Врач-реабилитолог отделения физиотерапии, лечебной физкультуры и массажа", kz: "Физиотерапия, емдік дене шынықтыру және массаж бөлімінің дәрігер-реабилитологы" }, department: "physiotherapy", order: 2 },
    
    // Реабилитация
    
    // Средний медицинский персонал
    { id: "tynyshtykova", name: { ru: "Тыныштықова Ляззат Елубаевна", kz: "Тыныштықова Ляззат Елубаевна" }, position: { ru: "Медицинская сестра", kz: "Мейірбике" }, department: "nursing", order: 1 },
    { id: "anes", name: { ru: "Анес Назерке Мендуллаевна", kz: "Анес Назерке Мендуллаевна" }, position: { ru: "Медицинская сестра / Сестринское дело", kz: "Медбике / Сестрин ісі" }, department: "nursing", order: 2 },
    { id: "kolkhozov", name: { ru: "Колхозов Айдар Оразбекович", kz: "Колхозов Айдар Оразбекович" }, position: { ru: "Фельдшер выездной бригады скорой медицинской помощи", kz: "Жедел жәрдем бригадасының фельдшері" }, department: "nursing", order: 3 },
    { id: "burambaeva", name: { ru: "Бурамбаева Зауре Амангельдиновна", kz: "Бурамбаева Зауре Амангельдиновна" }, position: { ru: "Медицинская сестра", kz: "Медициналық мейірбике" }, department: "diagnostics", order: 9 },
    { id: "fayzolda", name: { ru: "Файзолда Марзия", kz: "Файзолда Марзия" }, position: { ru: "Медицинская сестра", kz: "Мейірбике" }, department: "specialists", order: 8 },
    { id: "kokymbaeva", name: { ru: "Қоқымбаева Айгерім Бейбітжанқызы", kz: "Қоқымбаева Айгерім Бейбітжанқызы" }, position: { ru: "Медицинская сестра", kz: "Мейірбике" }, department: "specialists", order: 9 },
   
    // Функционалды диагностика бөлімі
    { id: "nurgalieva", name: { ru: "Нұрғалиева Зере Тілеубекқызы", kz: "Нұрғалиева Зере Тілеубекқызы" }, position: { ru: "Врач УЗИ", kz: "УЗИ дәрігері" }, department: "diagnostics", order: 1 },
  ];

  // Add all existing doctors to their departments
  doctors.forEach(doctor => {
    const staffMember = staff.find(s => s.id === doctor.id);
    
    if (staffMember) {
      const departmentId = staffMember.department;
      if (doctorsByDepartment[departmentId]) {
        doctorsByDepartment[departmentId].push({
          ...doctor,
          department: departmentId,
          order: staffMember.order
        });
      }
    }
  });

  // Add staff who are not already in the doctors array
  staff.forEach(staffMember => {
    const existingDoctor = doctors.find(d => d.id === staffMember.id);
    if (!existingDoctor && doctorsByDepartment[staffMember.department]) {
      doctorsByDepartment[staffMember.department].push({
        id: staffMember.id,
        name: staffMember.name,
        position: staffMember.position,
        department: staffMember.department,
        order: staffMember.order,
        isNewStaff: true
      });
    }
  });

  // Find Nigmetova Asemgul Altaevna
  const nigmetova = staff.find(s => s.id === 'nigmetova');
  const nigmetovaDoctor = doctors.find(d => d.id === 'nigmetova');
  const nigmetovaData = nigmetovaDoctor
    ? { ...nigmetovaDoctor, ...nigmetova, isNewStaff: false }
    : { ...nigmetova, isNewStaff: true };

  return (
    <div className="space-y-16">
      {/* Special group for Главная медсестра */}
      {nigmetovaData && (
        <div className="mb-12">
          <h2 className="text-2xl font-serif font-bold text-center mb-8">
            {language === 'ru' ? 'Главная медсестра' : 'Бас мейірбике'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 justify-center">
            <Card key={nigmetovaData.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
              <div className="relative h-48 flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#eaeaf0' }}>
                {nigmetovaData && 'image' in nigmetovaData && nigmetovaData.image ? (
                  <img 
                    src={nigmetovaData.image} 
                    alt={nigmetovaData.name[language]} 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <User size={64} className="text-gray-400" />
                )}
              </div>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-2">{nigmetovaData.name[language]}</h3>
                <p className="text-gray-600 text-sm mb-4">{nigmetovaData.position[language]}</p>
                {'experience' in nigmetovaData && nigmetovaData.experience && (
                  <div className="flex items-center text-sm text-gray-500 mb-4">
                    <span className="font-medium mr-2">{language === 'ru' ? 'Стаж:' : 'Тәжірибе:'}</span>
                    <span>{nigmetovaData.experience[language]}</span>
                  </div>
                )}
                {!nigmetovaData.isNewStaff && (
                  <Button 
                    onClick={() => onSelectDoctor(nigmetovaData.id)} 
                    variant="outline" 
                    className="w-full"
                  >
                    {language === 'ru' ? 'Подробнее' : 'Толығырақ'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
      {/* Display each department with its staff */}
      {departments
        .filter(dept => doctorsByDepartment[dept.id] && doctorsByDepartment[dept.id].length > 0)
        .sort((a, b) => a.order - b.order)
        .map((department) => (
          <div key={department.id} className="mb-12">
            <h2 className="text-2xl font-serif font-bold text-center mb-8">
              {language === 'ru' ? department.nameRu : department.nameKz}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {doctorsByDepartment[department.id]
                .sort((a, b) => (a.order || 999) - (b.order || 999))
                .map((doctor) => (
                <Card key={doctor.id} className="overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  <div className="relative h-48 flex items-center justify-center overflow-hidden" style={{ backgroundColor: '#eaeaf0' }}>
                    {doctor.image ? (
                      <img 
                        src={doctor.image} 
                        alt={doctor.name[language]} 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <User size={64} className="text-gray-400" />
                    )}
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{doctor.name[language]}</h3>
                    <p className="text-gray-600 text-sm mb-4">{doctor.position[language]}</p>
                    {doctor.experience && (
                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <span className="font-medium mr-2">{language === 'ru' ? 'Стаж:' : 'Тәжірибе:'}</span>
                        <span>{doctor.experience[language]}</span>
                      </div>
                    )}
                    {!doctor.isNewStaff && (
                      <Button 
                        onClick={() => onSelectDoctor(doctor.id)} 
                        variant="outline" 
                        className="w-full"
                      >
                        {language === 'ru' ? 'Подробнее' : 'Толығырақ'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

export default TeamList;
